import type { AdminRole, DeviceCommandType, AdminPermission } from '@hisob/types';

export const APP_NAME = 'Hisob Admin Console';

export const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  STAGING: 'staging',
  PRODUCTION: 'production',
} as const;

export const COMMAND_TYPE_LABELS: Record<DeviceCommandType, { label: string; description: string; dangerous: boolean }> = {
  export_snapshot: {
    label: 'Export Diagnostic Snapshot',
    description: 'Requests temporary encrypted diagnostic snapshot with user consent',
    dangerous: false,
  },
  clear_cache: {
    label: 'Clear Local Cache',
    description: 'Instructs device to purge non-persistent local cache files',
    dangerous: false,
  },
  refresh_config: {
    label: 'Refresh App Config',
    description: 'Forces device to re-fetch latest remote config and feature flags',
    dangerous: false,
  },
  refresh_entitlements: {
    label: 'Refresh Entitlements',
    description: 'Triggers a re-verification of active store entitlements and subscriptions',
    dangerous: false,
  },
  logout: {
    label: 'Graceful Logout',
    description: 'Terminates active device session and returns user to onboarding',
    dangerous: true,
  },
  invalidate_session: {
    label: 'Invalidate Session',
    description: 'Immediately revokes device access token and forces authentication',
    dangerous: true,
  },
  diagnostics: {
    label: 'Run Self-Diagnostics',
    description: 'Runs local integrity checks and submits non-financial metric report',
    dangerous: false,
  },
};

export const ROLE_INFO: Record<AdminRole, { label: string; badgeColor: string; description: string }> = {
  super_admin: {
    label: 'Super Admin',
    badgeColor: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    description: 'Unrestricted administrative access to all systems, features, and logs',
  },
  support: {
    label: 'Support Specialist',
    badgeColor: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
    description: 'Device diagnostics, support snapshot workflows, and user inspection',
  },
  developer: {
    label: 'Platform Developer',
    badgeColor: 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20',
    description: 'Remote config, feature flags rollout, maintenance mode controls',
  },
  auditor: {
    label: 'Compliance Auditor',
    badgeColor: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
    description: 'Read-only access to audit logs, security events, and system states',
  },
};

export const SENSITIVE_KEYS = [
  'token',
  'jwt',
  'secret',
  'password',
  'authorization',
  'pin',
  'cvv',
  'card',
  'refresh_token',
  'access_token',
];
